<template>
	<Page animate>
		<template v-slot:content>
			<img class="slider-paywall-image" src="@/assets/paywall/curved-bg.png" alt="" />
			<div class="container">
				<img class="slider-paywall-logo" src="@/assets/paywall/simple-prem-logo.svg" alt="" />
				<div class="slider-paywall-title" v-html="t('title')"></div>
				<div class="slider-paywall-price mb-md">
					<div class="slider-paywall-price-value">{{ activeProduct.price }}</div>
					<Badge v-show="activeProduct.badge" class="slider-paywall-badge" :theme="activeProduct.badgeTheme"
						>{{ activeProduct.badge }}
					</Badge>
				</div>
				<div class="slider-paywall-slider-wrapper" :class="className">
					<Slider
						:min="0"
						:max="2"
						:lazy="false"
						:model-value="sliderVal"
						@update="onChange"
						:options="{ pips: { density: 3, values: [0, 50, 100], mode: 'positions' } }"
						:tooltips="false"
					/>
				</div>
				<div class="slider-paywall-terms">{{ activeProduct.subtitle }}</div>

				<div class="links">
					<a @click.prevent="terms" href="">{{ t('terms') }}</a>
					<span></span>
					<a @click.prevent="privacy" href="">{{ t('privacy') }}</a>
					<span></span>
					<a @click.prevent="handleRestore" href="">{{ t('restore') }}</a>
				</div>
			</div>

			<Alert
				:isOpen="showRestoreSuccessAlert"
				:title="t('restore_popup.success.title')"
				:text="t('restore_popup.success.body')"
				@onClose="onCloseRestoreSuccessAlert"
			/>
			<Alert
				:isOpen="showRestoreErrorAlert"
				:title="t('restore_popup.error.title')"
				:text="t('restore_popup.error.body')"
				@onClose="onCloseRestoreErrorAlert"
			/>
		</template>

		<template v-slot:footer>
			<div class="container">
				<p-button @click.prevent="handleSubmit" label="" :loading="isLoading" cls="btn-primary btn-block mb-md">
					{{ t('buttonAccept') }}
				</p-button>
				<a href="" class="btn ttn btn-block btn-text btn-color-lightgray" @click.prevent="close">
					{{ t('buttonDecline') }}
				</a>
			</div>
		</template>
	</Page>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import Slider from '@vueform/slider'
import { inject, ref, watch } from '@vue/runtime-core'
import { computed } from '@vue/reactivity'
import Page from '@/components/Page'
import Alert from '@/components/Alert'
import PButton from '@/components/Button'
import Badge from '@/components/Badge'
import useConfig from '@/composition/config'
import useApi from '@/composition/api'

const config = useConfig()
const store = useStore()
const api = useApi()
const amplitude = inject('amplitude')

const { terms, privacy } = api
const isLoading = computed(() => store.state.isLoading)
const showRestoreSuccessAlert = ref(false)
const showRestoreErrorAlert = ref(false)
const showPaywallCancelPrompt = ref(false)

const { t } = useI18n({
	useScope: 'global',
	messages: {
		en: {
			title: 'Pay what you want to <span class="text-primary">unlock everything</span>',
			buttonAccept: 'CONTINUE',
			buttonDecline: 'Decline',
			products: {
				one: {
					price: '[price]/1 month',
					subtitle: '[weekly_price] per week, a monthly subscription fee is [price] and auto-renews every month'
				},
				two: {
					price: '[price]/12 weeks',
					subtitle: '[weekly_price] per week, a 12-week subscription fee is [price] and auto-renews each 12 weeks',
					badge: 'Popular'
				},
				three: {
					price: '[price]/year',
					subtitle: '[weekly_price] per week, an annual subscription fee is [price] and auto-renews yearly',
					badge: 'save 58%'
				}
			}
		}
	}
})

const products = computed(() => {
	const firstProduct = Object.assign(
		{
			id: 'simple_original_subscription_1_month',
			price: t('products.one.price'),
			badge: null,
			badgeTheme: null,
			subtitle: t('products.one.subtitle')
		},
		config.get({ key: 'paywall.first_product', default_value: {} })
	)

	const secondProduct = Object.assign(
		{
			id: 'simple_original_subscription_3_months',
			price: t('products.two.price'),
			badge: t('products.two.badge'),
			badgeTheme: 'default',
			subtitle: t('products.two.subtitle')
		},
		config.get({ key: 'paywall.second_product', default_value: {} })
	)

	const thirdProduct = Object.assign(
		{
			id: 'simple_original_subscription_12_months',
			price: t('products.three.price'),
			badge: t('products.three.badge'),
			badgeTheme: 'primary',
			subtitle: t('products.three.subtitle')
		},
		config.get({ key: 'paywall.third_product', default_value: {} })
	)

	return store.getters['products/getData']([firstProduct, secondProduct, thirdProduct])
})

const activeProduct = computed(() => {
	return products.value[_sliderVal.value]
})

const sliderVal = ref(1)
const _sliderVal = ref(sliderVal.value)
const className = ref('posOne')
let tmID = null

function onChange(v) {
	_sliderVal.value = v
	clearTimeout(tmID)
	tmID = setTimeout(() => {
		sliderVal.value = v
	}, 300)
}

watch(_sliderVal, (newVal, oldVal) => {
	if (newVal === 0) {
		className.value = oldVal === 1 ? 'posFromTwoToOne' : 'posOne'
	}
	if (newVal === 1) {
		className.value = oldVal === 2 ? 'posFromThreeToTwo' : 'posTwo'
	}
	if (newVal === 2) {
		className.value = oldVal === 0 ? 'posFromOneToThree' : 'posThree'
	}
})

function close(Status = 'Close') {
	amplitude.logEvent('Paywall - Close', {
		Layout: config.get({ key: 'layout', default_value: 'Slider Paywall' }),
		From: store.getters['getSource'],
		Step: 'Paywall',
		Status
	})
	api.close()
}

function handleRestore() {
	api.restore({
		closeOnSuccess: false,
		onFinish: () => {
			showRestoreSuccessAlert.value = true
		},
		onFail: () => {
			showRestoreErrorAlert.value = true
		}
	})
}

function onCloseRestoreSuccessAlert() {
	showRestoreErrorAlert.value = false
	close('Restore')
}

function onCloseRestoreErrorAlert() {
	showRestoreErrorAlert.value = false
}

const Purchase = (product) => {
	api.purchase({
		productId: product.id,
		onFinish: () => {
			amplitude.logEvent('Paywall - Done', {
				Layout: config.get({ key: 'layout', default_value: 'Slider Paywall' }),
				From: store.getters['getSource'],
				Step: 'Paywall',
				ProductId: product.id,
				Status: 'Subscribe'
			})
			close('Subscribe')
		},
		onError: () => {
			console.log('error')
		},
		onCancel: () => {
			showPaywallCancelPrompt.value = true
		},
		closeOnSuccess: false
	})
}

function handleSubmit() {
	Purchase(activeProduct.value)
}
</script>

<style src="@vueform/slider/themes/default.css"></style>
<style>
:root {
	--slider-connect-bg: #7d8bf7;
	--slider-height: 3px;
	--slider-bg: #e2e3ee;
	--slider-handle-bg: #7d8bf7;
	--slider-handle-shadow: none;
	--slider-handle-shadow-active: 0.5px 0.5px 2px 1px rgba(0, 0, 0, 0.22);
	--slider-handle-width: 24px;
	--slider-handle-height: 24px;
}
</style>
<style lang="scss" scoped>
.slider-paywall-image {
	max-width: 500px;
	width: 100%;
	display: block;
	margin-left: auto;
	margin-right: auto;
	margin-bottom: 2rem;
}

$color: #7d8bf7;
:deep .slider-value {
	position: absolute;
	top: -3px;
	transform: translateX(-50%);
	display: block;
	width: 8px;
	height: 8px;
	background-color: #e2e3ee;
	border-radius: 50%;
	color: transparent;
	transition: all 250ms;
}

:deep .slider-base::before {
	content: '';
	position: absolute;
	height: 24px;
	width: 100%;
	top: -11px;
}

.posOne {
	:deep [data-value='0'] {
		background-color: $color;
	}
}

.posTwo {
	:deep {
		[data-value='0'] {
			background-color: $color;
		}
	}
}

.posFromThreeToTwo {
	:deep {
		[data-value='1'] {
			transition: none;
			background-color: #e2e3ee;
		}

		[data-value='0'],
		[data-value='1'] {
			background-color: $color;
			transition-delay: 250ms;
		}
	}
}

.posFromTwoToOne {
	:deep {
		[data-value='1'] {
			transition: none;
			background-color: #e2e3ee;
		}

		[data-value='0'] {
			background-color: $color;
		}
	}
}

.posFromOneToThree {
	:deep {
		[data-value='1'],
		[data-value='0'] {
			background-color: $color;
		}
	}
}

.posThree {
	:deep {
		[data-value='1'] {
			transition: none;
		}

		[data-value='0'],
		[data-value='1'] {
			background-color: $color;
		}
	}
}

.slider-paywall-logo {
	display: block;
	height: 31px;
	width: auto;
	margin-left: auto;
	margin-right: auto;
	margin-bottom: 1rem;
}

.slider-paywall-title {
	text-align: center;
	font-size: 1.9rem;
	line-height: 1.2;
	font-weight: bold;

	margin-bottom: 1.2rem;

	span {
		display: block;
	}
}

.slider-paywall-price {
	display: flex;
	align-items: center;
	justify-content: center;
}

.slider-paywall-badge {
	margin-left: 0.85rem;
}

.slider-paywall-price-value {
	font-weight: 700;
	font-size: 1.15rem;
}

.slider-paywall-slider-wrapper {
	padding-top: 12px;
	min-height: 24px;
	padding-left: 10px;
	padding-right: 10px;
	margin-bottom: 1.1rem;
}

.slider-paywall-terms {
	text-align: center;
	font-size: 0.9rem;
}

.links {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 2rem;
	flex-wrap: wrap;

	span {
		width: 3px;
		height: 3px;
		border-radius: 50%;
		background-color: #3b3c57;
		margin: 0 12px;
		opacity: 0.5;
	}

	a {
		text-decoration: none;
		color: #3b3c57;
		font-size: 0.9rem;
		display: block;
		margin: 2px 0;
	}
}
</style>
